import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { isRtlCurrency } from "@/lib/currency";
import { useAuth } from "@/contexts/AuthContext";

/**
 * DirectionContext — controls the document's `dir` attribute (ltr/rtl).
 *
 * Preference order:
 *   1. User-set override in localStorage (`pfo_direction` = "ltr" | "rtl" | "auto")
 *   2. Default = `ltr` — left-to-right for every new user. Currency-based
 *      RTL is now opt-in via the Settings > Direction toggle.
 *
 * We deliberately keep this focused on layout direction only; language
 * translation is a separate concern.
 */

const DirectionContext = createContext(null);
const STORAGE_KEY = "pfo_direction";
// Bump this key when we want to force-migrate existing users to a new default.
// Users who explicitly set ltr/rtl in Settings AFTER the migration are respected.
const MIGRATION_KEY = "pfo_direction_migration_v1";

function readInitial() {
  try {
    // One-time migration: any previous "auto" default gets snapped to "ltr".
    const migrated = localStorage.getItem(MIGRATION_KEY);
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!migrated) {
      if (!stored || stored === "auto") {
        localStorage.setItem(STORAGE_KEY, "ltr");
        localStorage.setItem(MIGRATION_KEY, "1");
        return "ltr";
      }
      localStorage.setItem(MIGRATION_KEY, "1");
    }
    if (stored === "ltr" || stored === "rtl" || stored === "auto") return stored;
  } catch { /* ignore */ }
  return "ltr";
}

export function DirectionProvider({ children }) {
  const [preference, setPreferenceState] = useState(readInitial);
  const { user } = useAuth();

  const effective = useMemo(() => {
    if (preference === "ltr" || preference === "rtl") return preference;
    // auto — derive from currency
    return isRtlCurrency(user?.currency) ? "rtl" : "ltr";
  }, [preference, user?.currency]);

  useEffect(() => {
    document.documentElement.setAttribute("dir", effective);
  }, [effective]);

  const setPreference = useCallback((next) => {
    setPreferenceState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }, []);

  return (
    <DirectionContext.Provider value={useMemo(
      () => ({ preference, effective, setPreference }),
      [preference, effective, setPreference],
    )}>
      {children}
    </DirectionContext.Provider>
  );
}

export const useDirection = () => {
  const ctx = useContext(DirectionContext);
  if (!ctx) throw new Error("useDirection must be used inside <DirectionProvider>");
  return ctx;
};
